import React from 'react';
import { FormattedMessage } from '../../../util/reactIntl';
import { ListingCard, ListingCardSkeletons, NamedLink } from '../../../components';

import { IconArrow } from './Icons';
import ScrollRow from './ScrollRow';
import VerifiedBadge from './VerifiedBadge';
import css from './SectionProductRow.module.css';

const SKELETON_COUNT = 6;

// A single "coming soon" placeholder tile. These keep every category row
// looking full and intentional before vendors have published products in
// that category — and each one links straight into the relevant search, so
// the row is useful even while empty. As real listings arrive they take the
// place of these placeholders automatically.
const PlaceholderTile = ({ search }) => (
  <NamedLink name="SearchPage" to={{ search }} className={css.placeholder}>
    <span className={css.placeholderArt} aria-hidden="true">
      <VerifiedBadge className={css.placeholderBadge} />
    </span>
    <span className={css.placeholderLabel}>
      <FormattedMessage
        id="LandingPage.productRow.placeholder"
        defaultMessage="New products with verified credentials coming soon"
      />
    </span>
  </NamedLink>
);

/**
 * A titled row of products for a single Listing Category.
 *
 * The category comes from the hosted Sharetribe config, and listings are matched
 * on the structured category value stored in their public data (rather than on
 * words in their title), so a product filed under a category always appears in
 * its row. Any remaining slots are filled with branded "coming soon"
 * placeholders, so the row looks complete while a category is still thin and
 * fills itself in as vendors publish.
 *
 * The section is always exactly one row tall: extra tiles are reached by
 * scrolling sideways (arrow buttons or a swipe) rather than by wrapping onto a
 * second line. A "View all" button in the bottom right corner drops the user
 * into the marketplace search with this category already filtered.
 *
 * @param {Object} props
 * @param {{id: string, name: string}} props.category hosted listing category
 * @param {string} [props.categoryKey] category param prefix, e.g. 'categoryLevel'
 * @param {Array<Object>} [props.listings] listings already loaded for the page
 * @param {number} [props.count] tiles in the row
 */
const SectionProductRow = props => {
  const {
    category,
    categoryKey = 'categoryLevel',
    listings = [],
    count = 10,
    queryInProgress = false,
  } = props;
  const { id, name } = category || {};

  // A listing belongs to this row if the category is selected at any level, so a
  // product filed under a subcategory still shows up under its parent.
  const isInCategory = listing => {
    const publicData = listing?.attributes?.publicData || {};
    return Object.entries(publicData).some(
      ([key, value]) => key.startsWith(categoryKey) && value === id
    );
  };

  const realTiles = listings.filter(isInCategory).slice(0, count);
  const placeholderCount = Math.max(0, count - realTiles.length);
  const search = `?pub_${categoryKey}1=${encodeURIComponent(id)}`;

  return (
    <section className={css.root}>
      <div className={css.inner}>
        <header className={css.header}>
          <h2 className={css.title}>{name}</h2>
        </header>

        {/* While products are loading the row shows card-shaped skeletons,
            not "coming soon" tiles that flash and are then replaced. */}
        <ScrollRow label={name}>
          {queryInProgress
            ? ListingCardSkeletons({ count: SKELETON_COUNT })
            : [
                ...realTiles.map(l => (
                  <ListingCard key={l.id.uuid} className={css.card} listing={l} />
                )),
                ...Array.from({ length: placeholderCount }).map((_, i) => (
                  <PlaceholderTile key={`ph-${i}`} search={search} />
                )),
              ]}
        </ScrollRow>

        <div className={css.footer}>
          <NamedLink name="SearchPage" to={{ search }} className={css.viewAll}>
            <FormattedMessage id="LandingPage.productRow.viewAll" defaultMessage="View all" />
            <IconArrow className={css.viewAllArrow} />
          </NamedLink>
        </div>
      </div>
    </section>
  );
};

export default SectionProductRow;
